import '@/styles/globals.css';
import '@/components/auth/authFetchDiagnostics';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { NextIntlClientProvider } from 'next-intl';
import { SessionProvider } from 'next-auth/react';
import { AzureAppInsights } from '@/components/azureAppInsights/AzureAppInsights';
import { NotificationProvider } from '@/components/general/NotificationProvider';
import NotificationContainer from '@/components/general/NotificationContainer';
import { useEffect, useRef } from 'react';
import TopLoader from '@/components/general/TopLoader';
import { AuthSessionWatcher } from '@/components/auth/AuthSessionWatcher';
import { AuthRedirectLockCleaner } from '@/components/auth/AuthRedirectLockCleaner';

export default function App({ Component, pageProps: { session, messages, ...pageProps } }: AppProps) {
  const router = useRouter();
  const isBackNavigation = useRef(false);
  const hasRestoredOnLoad = useRef(false);

  useEffect(() => {
    // Let us fully control scroll (browser won't auto-scroll on back/forward)
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const saveScrollPosition = (url: string) => {
      try {
        sessionStorage.setItem(`scroll-pos:${url}`, JSON.stringify({ x: window.scrollX, y: window.scrollY }));
      } catch {
        // ignore
      }
    };

    const restoreScrollPosition = (url: string) => {
      try {
        const raw = sessionStorage.getItem(`scroll-pos:${url}`);
        if (!raw) return false;

        const { x, y } = JSON.parse(raw) as { x?: number; y?: number };

        // Ensure we apply scroll AFTER the new page has rendered
        requestAnimationFrame(() => {
          window.scrollTo(x ?? 0, y ?? 0);

          setTimeout(() => {
            const diff = Math.abs((window.scrollY ?? 0) - (y ?? 0));
            if (diff > 100) {
              window.scrollTo(x ?? 0, y ?? 0);
            }
          }, 500);
        });

        return true;
      } catch {
        return false;
      }
    };

    // ✅ Only once, on initial load / hard refresh
    if (!hasRestoredOnLoad.current) {
      restoreScrollPosition(router.asPath); // or remove this line if you *never* want refresh restore
      hasRestoredOnLoad.current = true;
    }

    // Detect browser back/forward
    router.beforePopState(() => {
      isBackNavigation.current = true;
      return true;
    });

    const handleRouteChangeStart = () => {
      // Save the scroll for the page we are leaving
      saveScrollPosition(router.asPath);
    };

    const handleRouteChangeComplete = (url: string) => {
      if (isBackNavigation.current) {
        // 🔙 Back/forward: restore position if we have it
        restoreScrollPosition(url);
      }

      isBackNavigation.current = false;
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.beforePopState(() => true);
    };
  }, [router]);

  return (
    <NextIntlClientProvider locale={router.locale} messages={messages}>
      <AzureAppInsights>
        <SessionProvider session={session} refetchOnWindowFocus={true}>
          <AuthSessionWatcher />
          <AuthRedirectLockCleaner />
          <NotificationProvider>
            <TopLoader />
            <Component {...pageProps} />
            <NotificationContainer />
          </NotificationProvider>
        </SessionProvider>
      </AzureAppInsights>
    </NextIntlClientProvider>
  );
}
